/**
 * Klavox Send (R15) cote client : partage PONCTUEL d'un secret, sans compte
 * pour le destinataire, sans que le serveur puisse lire quoi que ce soit.
 *
 * Tout ce qui est cryptographique vient de `@klavox/crypto-core` (`send.ts`) ;
 * tout ce qui est reseau vient de `sync-client.ts`. Ce module ne fait que les
 * assembler -- et tenir LA regle qui donne son sens a la fonction :
 *
 *   la cle de l'envoi vit dans le FRAGMENT `#` du lien, que les navigateurs
 *   n'envoient jamais au serveur. Elle n'apparait donc ni dans une requete,
 *   ni dans un journal d'acces, ni dans un `Referer`.
 *
 * Le lien est construit sur l'origine du SERVEUR de synchro : c'est lui qui
 * servira l'envoi au destinataire, et le coffre web est servi par la meme
 * origine dans un deploiement Klavox (`apps/server`, `apps/web`).
 */
import {
  bytesToBase64,
  base64ToBytes,
  DEFAULT_KDF_PARAMS,
  deriveSendKeyAsync,
  formatSendSecret,
  generateSendSalt,
  generateSendSecret,
  openSend,
  parseSendSecret,
  sealSend,
  type KdfParams,
  type SendPayload,
} from '@klavox/crypto-core';
import { generateId } from '@klavox/vault-model';
import {
  accessSend,
  createSend,
  describeSend,
  listSends,
  revokeSend,
  updateSend,
  type OwnedSendSummary,
  type SendContent,
  type SendDescription,
} from './sync-client.js';

/** Durees de vie proposees dans l'interface. Le serveur plafonne a 30 jours. */
export const SEND_EXPIRY_CHOICES: ReadonlyArray<{ readonly label: string; readonly ms: number }> = [
  { label: '1 heure', ms: 60 * 60 * 1000 },
  { label: '1 jour', ms: 24 * 60 * 60 * 1000 },
  { label: '2 jours', ms: 2 * 24 * 60 * 60 * 1000 },
  { label: '3 jours', ms: 3 * 24 * 60 * 60 * 1000 },
  { label: '7 jours', ms: 7 * 24 * 60 * 60 * 1000 },
  { label: '30 jours', ms: 30 * 24 * 60 * 60 * 1000 },
];

/** Duree de vie maximale d'un envoi : celle du serveur (`MAX_SEND_TTL_MS`), 30 jours comme Bitwarden Send. */
export const MAX_SEND_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Nombre maximal de telechargements d'un envoi : celui du serveur (`MAX_SEND_VIEWS`). */
export const MAX_SEND_VIEWS = 10_000;

/** `AAAA-MM-JJTHH:MM` en heure locale, la forme d'un `<input type="datetime-local">`. */
export function toLocalDateTimeInput(ms: number): string {
  const d = new Date(ms);
  const two = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}T${two(d.getHours())}:${two(d.getMinutes())}`;
}

/**
 * Echeance personnalisee (« Deletion date » personnalisee de Bitwarden Send) : la date saisie, en heure locale,
 * devient une duree de vie. Refusee si illisible, passee ou au-dela de 30 jours -- jamais rabotee en silence :
 * l'emetteur doit savoir quand son lien cesse de marcher.
 */
export function resolveCustomSendExpiry(local: string, now: number): number {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/u.test(local)) throw new Error('date d’expiration illisible');
  const at = new Date(local).getTime();
  if (!Number.isFinite(at)) throw new Error('date d’expiration illisible');
  if (at <= now) throw new Error('date d’expiration deja passee');
  if (at - now > MAX_SEND_TTL_MS) throw new Error('date d’expiration au-dela de 30 jours');
  return at - now;
}

/**
 * Nombre de telechargements autorises (« Maximum access count » de Bitwarden Send) : vide = sans limite, sinon un
 * entier de 1 a 10 000. Un zero, un decimal ou un texte est refuse, jamais lu comme « sans limite ».
 */
export function parseSendMaxViews(raw: string): number | null {
  const value = raw.trim();
  if (value === '') return null;
  if (!/^\d+$/u.test(value)) throw new Error('nombre de telechargements invalide');
  const views = Number(value);
  if (views < 1 || views > MAX_SEND_VIEWS) throw new Error(`nombre de telechargements : de 1 a ${MAX_SEND_VIEWS}`);
  return views;
}

/** Taille maximale d'un fichier partage (en clair). Aligne sur les pieces jointes du coffre. */
export const MAX_SEND_FILE_BYTES = 10 * 1024 * 1024;

/** Note privee de l'emetteur : 1000 caracteres, comme Bitwarden Send. */
export const MAX_SEND_NOTE_LENGTH = 1000;

/** Prefixe de route du lien de partage, dans le FRAGMENT : `#/send/<id>/<secret>`. */
const SEND_ROUTE = '#/send/';

/**
 * Acces au scellement sous la `vaultKey`, reduit a ce dont Klavox Send a
 * besoin : `VaultStore` le satisfait sans jamais preter sa cle.
 */
export interface SendReceiptSealer {
  sealSendReceipt(sendId: string, receipt: unknown): string;
  openSendReceipt<T>(sendId: string, blob: string): T;
}

/**
 * Ce que l'emetteur garde de son envoi, scelle sous sa `vaultKey` et confie au
 * serveur (qui n'en voit qu'un blob) : de quoi retrouver son propre lien plus
 * tard, sans que le serveur puisse jamais le reconstituer.
 */
interface SendOwnerSecrets {
  readonly label: string;
  /** Secret d'URL en base64url -- la moitie que le serveur n'a pas. */
  readonly secret: string;
  readonly origin: string;
  /** Note privee (« Private notes » de Bitwarden Send) : pour l'emetteur seul, absente si vide. */
  readonly note?: string;
}

/** Envoi de l'emetteur, une fois son blob ouvert sous la `vaultKey`. */
export interface OwnedSend {
  readonly id: string;
  readonly label: string;
  /** Note privee de l'emetteur ; '' si aucune. Jamais dans le contenu du destinataire. */
  readonly note: string;
  /** Lien de partage complet, reconstruit localement. */
  readonly shareUrl: string;
  readonly createdAt: number;
  readonly expiresAt: number;
  readonly maxViews: number | null;
  readonly views: number;
  readonly revokedAt: number | null;
  readonly passwordProtected: boolean;
  /** `false` des que l'envoi est revoque, expire ou epuise. */
  readonly active: boolean;
}

export interface CreateSendInput {
  readonly baseUrl: string;
  readonly sessionToken: string;
  readonly vault: SendReceiptSealer;
  /** Intitule visible par le destinataire APRES dechiffrement -- jamais par le serveur. */
  readonly label: string;
  readonly payload: SendPayload;
  /** Note privee : scellee avec le recu de l'emetteur, ni dans le contenu ni lisible du serveur. */
  readonly note?: string;
  readonly expiresInMs: number;
  readonly maxViews: number | null;
  /** Mot de passe OPTIONNEL, EXIGE EN PLUS du lien (jamais a la place). */
  readonly password?: string;
  readonly kdfParams?: KdfParams;
}

export interface CreatedSend {
  readonly id: string;
  readonly shareUrl: string;
  readonly expiresAt: number;
}

/** Origine servant le lien de partage : celle du serveur souverain qui hebergera l'envoi. */
export function sendOrigin(baseUrl: string): string {
  return new URL(baseUrl).origin;
}

/** `https://…/#/send/<id>/<secret>` — le secret est APRES le `#`, donc jamais transmis. */
export function buildSendLink(origin: string, id: string, secretBase64Url: string): string {
  return `${origin.replace(/\/+$/u, '')}/${SEND_ROUTE}${encodeURIComponent(id)}/${secretBase64Url}`;
}

/** Route d'envoi reconnue dans un fragment d'URL, sinon `null`. */
export interface SendLink {
  readonly id: string;
  readonly secretBase64Url: string;
}

/**
 * Relit un lien de partage. Accepte une URL complete ou un simple fragment
 * (`#/send/…`), ce qui permet de brancher directement `location.hash`.
 */
export function parseSendLink(href: string): SendLink | null {
  if (typeof href !== 'string') return null;
  const hash = href.startsWith('#') ? href : (href.split('#')[1] ?? '');
  const fragment = hash.startsWith('#') ? hash.slice(1) : hash;
  const match = /^\/send\/([^/]+)\/([A-Za-z0-9_-]+)$/u.exec(fragment);
  if (match === null) return null;
  return { id: decodeURIComponent(match[1]!), secretBase64Url: match[2]! };
}

/** Note privee rognee, refusee au-dela de 1000 caracteres -- la meme garde a la creation et a la modification. */
function readSendNote(raw: string): string {
  const note = raw.trim();
  if (note.length > MAX_SEND_NOTE_LENGTH) throw new Error(`note privee trop longue (${MAX_SEND_NOTE_LENGTH} caracteres maximum)`);
  return note;
}

/**
 * Prepare, scelle et depose un envoi.
 *
 * Deux scellements, deux cles, aucun recoupement possible cote serveur :
 *  - le CONTENU sous la cle de l'envoi (secret d'URL + mot de passe eventuel) ;
 *  - le REPERE de l'emetteur (libelle + secret d'URL) sous sa `vaultKey`.
 */
export async function createVaultSend(input: CreateSendInput): Promise<CreatedSend> {
  const note = readSendNote(input.note ?? '');
  const id = generateId();
  const secret = generateSendSecret();
  const salt = generateSendSalt();
  const hasPassword = typeof input.password === 'string' && input.password.length > 0;
  const kdfParams = hasPassword ? (input.kdfParams ?? DEFAULT_KDF_PARAMS) : null;
  const origin = sendOrigin(input.baseUrl);
  try {
    const sendKey = await deriveSendKeyAsync({
      secret,
      salt,
      ...(hasPassword ? { password: input.password!, kdfParams: kdfParams! } : {}),
    });
    try {
      const ciphertext = sealSend({ sendKey, sendId: id, payload: input.payload });
      const owner: SendOwnerSecrets = { label: input.label, secret: formatSendSecret(secret), origin, ...(note.length > 0 ? { note } : {}) };
      const expiresAt = Date.now() + input.expiresInMs;
      await createSend(input.baseUrl, input.sessionToken, {
        id,
        ciphertext,
        secrets: input.vault.sealSendReceipt(id, owner),
        saltBase64: bytesToBase64(salt),
        kdfParams,
        expiresAt,
        maxViews: input.maxViews,
      });
      return { id, shareUrl: buildSendLink(origin, id, formatSendSecret(secret)), expiresAt };
    } finally {
      sendKey.destroy();
    }
  } finally {
    // Le secret ne survit pas a la creation cote emetteur : sa seule copie
    // durable est le blob scelle sous la `vaultKey`, et le lien qu'il a copie.
    secret.destroy();
  }
}

/** Envois de l'emetteur, blobs ouverts sous sa `vaultKey`. Un blob illisible est ignore, jamais devine. */
export async function listVaultSends(
  baseUrl: string,
  sessionToken: string,
  vault: SendReceiptSealer,
  now = Date.now(),
): Promise<readonly OwnedSend[]> {
  const summaries = await listSends(baseUrl, sessionToken);
  return summaries.flatMap((summary) => {
    let owner: SendOwnerSecrets;
    try {
      owner = vault.openSendReceipt<SendOwnerSecrets>(summary.id, summary.secrets);
    } catch {
      // Blob d'un autre coffre, altere, ou format inconnu : on n'affiche RIEN
      // plutot qu'un libelle invente.
      return [];
    }
    return [toOwnedSend(summary, owner, now)];
  });
}

function toOwnedSend(summary: OwnedSendSummary, owner: SendOwnerSecrets, now: number): OwnedSend {
  const exhausted = summary.maxViews !== null && summary.views >= summary.maxViews;
  return {
    id: summary.id,
    label: owner.label,
    note: typeof owner.note === 'string' ? owner.note : '',
    shareUrl: buildSendLink(owner.origin, summary.id, owner.secret),
    createdAt: summary.createdAt,
    expiresAt: summary.expiresAt,
    maxViews: summary.maxViews,
    views: summary.views,
    revokedAt: summary.revokedAt,
    passwordProtected: summary.passwordProtected,
    active: summary.revokedAt === null && summary.expiresAt > now && !exhausted,
  };
}

export interface UpdateSendInput {
  readonly baseUrl: string;
  readonly sessionToken: string;
  readonly vault: SendReceiptSealer;
  /** L'envoi tel que `listVaultSends` l'a rendu : son libelle et son lien sont re-scelles tels quels. */
  readonly send: OwnedSend;
  readonly expiresInMs: number;
  readonly maxViews: number | null;
  readonly note: string;
}

/**
 * Modifie un envoi depose (« Edit Send » de Bitwarden) : echeance, plafond de telechargements, note privee.
 * Le lien ne change pas -- le destinataire garde celui qu'il a recu ; le contenu et le mot de passe non plus.
 * Le repere de l'emetteur est re-scelle sous sa `vaultKey` avec la nouvelle note : le serveur ne voit qu'un blob neuf.
 */
export async function updateVaultSend(input: UpdateSendInput): Promise<{ readonly expiresAt: number }> {
  const note = readSendNote(input.note);
  if (input.maxViews !== null && (!Number.isInteger(input.maxViews) || input.maxViews < 1 || input.maxViews > MAX_SEND_VIEWS)) {
    throw new Error(`nombre de telechargements : de 1 a ${MAX_SEND_VIEWS}`);
  }
  if (!(input.expiresInMs > 0) || input.expiresInMs > MAX_SEND_TTL_MS) throw new Error('date d’expiration au-dela de 30 jours');
  const link = parseSendLink(input.send.shareUrl);
  if (link === null || link.id !== input.send.id) throw new Error('lien de l’envoi illisible');
  const owner: SendOwnerSecrets = {
    label: input.send.label,
    secret: link.secretBase64Url,
    origin: new URL(input.send.shareUrl).origin,
    ...(note.length > 0 ? { note } : {}),
  };
  const expiresAt = Date.now() + input.expiresInMs;
  await updateSend(input.baseUrl, input.sessionToken, input.send.id, {
    expiresAt,
    maxViews: input.maxViews,
    secrets: input.vault.sealSendReceipt(input.send.id, owner),
  });
  return { expiresAt };
}

/** Revoque un envoi : le lien cesse immediatement de servir, pour tout le monde. */
export async function revokeVaultSend(baseUrl: string, sessionToken: string, id: string): Promise<void> {
  await revokeSend(baseUrl, sessionToken, id);
}

// --- Cote DESTINATAIRE (aucun compte, aucun coffre) ---------------------

/** Etat d'un envoi, lu sans consommer de vue -- pour savoir s'il faut demander un mot de passe. */
export async function describeVaultSend(baseUrl: string, id: string): Promise<SendDescription> {
  return describeSend(baseUrl, id);
}

/**
 * Telecharge le ciphertext d'un envoi. CONSOMME UNE VUE : a n'appeler qu'une
 * fois, puis garder le resultat en memoire pour reessayer un mot de passe --
 * un mot de passe mal tape ne doit pas couter le seul telechargement disponible.
 */
export async function fetchVaultSend(baseUrl: string, id: string): Promise<SendContent> {
  return accessSend(baseUrl, id);
}

/**
 * Ouvre un envoi telecharge, avec le secret du lien et le mot de passe eventuel.
 *
 * @throws {DecryptionFailedError} lien errone, mot de passe faux ou contenu
 *   altere -- indistinguables, comme partout dans Klavox.
 */
export async function openVaultSend(
  content: SendContent,
  secretBase64Url: string,
  password?: string,
): Promise<SendPayload> {
  const secret = parseSendSecret(secretBase64Url);
  try {
    const hasPassword = content.kdfParams !== null;
    if (hasPassword && (password === undefined || password.length === 0)) {
      throw new SendPasswordRequiredError();
    }
    const sendKey = await deriveSendKeyAsync({
      secret,
      salt: base64ToBytes(content.saltBase64),
      ...(hasPassword ? { password: password!, kdfParams: content.kdfParams! } : {}),
    });
    try {
      return openSend({ sendKey, sendId: content.id, ciphertext: content.ciphertext });
    } finally {
      sendKey.destroy();
    }
  } finally {
    secret.destroy();
  }
}

/** L'envoi est protege par un mot de passe que le destinataire n'a pas (encore) fourni. */
export class SendPasswordRequiredError extends Error {
  override readonly name = 'SendPasswordRequiredError';

  constructor(message = 'cet envoi est protege par un mot de passe') {
    super(message);
  }
}
